import React, { useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { addDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import InfoModal from "../../components/InfoModal";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { auth, db } from "../../firebase";
import featureGatingService from "../../services/featureGatingService";
import { buildNgtCaseData } from "./ngtCaseMapper";
import { fetchUserMobile, scheduleHearingReminders } from "./ngtReminders";
import { uploadNgtOrderPdfs } from "./ngtFileSync";

// Friendly labels for the caseInfo object keys
const CASE_INFO_LABELS = {
  caseNumber: "Case Number",
  filingNumber: "Filing / Diary Number",
  filingDate: "Filing Date",
  registeredOn: "Registered On",
  lastListed: "Last Listed",
  partyName: "Party Name",
  petitionerAdvocate: "Petitioner Advocate",
  respondentAdvocate: "Respondent Advocate",
  act: "Act",
  caseStatus: "Case Status",
};

const InfoRow = ({ label, value }) => (
  <div className="flex py-1">
    <span className="w-48 text-sm text-slate-600">{label}:</span>
    <span className="font-semibold flex-1 break-words">{value || "N/A"}</span>
  </div>
);

function NGTCaseDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const { caseDetails, listItem = {}, searchType = "party-name", cookies = null } =
    location.state || {};

  const [exporting, setExporting] = useState(false);
  const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "" });
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({ currentCount: 0, maxLimit: 0, planName: "" });

  if (!caseDetails) {
    return (
      <>
                                <div className="text-slate-500 text-center mt-10">No case details available</div>
      </>
    );
  }

  const caseInfo = caseDetails?.caseInfo || {};
  const parties = caseDetails?.parties || {};
  const petitioners = Array.isArray(parties.petitioners) ? parties.petitioners : [];
  const respondents = Array.isArray(parties.respondents) ? parties.respondents : [];
  const listingHistory = Array.isArray(caseDetails?.listingHistory) ? caseDetails.listingHistory : [];
  const iaMa = Array.isArray(caseDetails?.iaMa) ? caseDetails.iaMa : [];
  const connectedMatters = Array.isArray(caseDetails?.connectedMatters)
    ? caseDetails.connectedMatters
    : [];

  const partyTitle = caseInfo.partyName || listItem.partyName || "Case Details";

  const handleViewPdf = (pdfPath) => {
    if (!pdfPath) {
      alert("No order/judgement PDF available for this listing.");
      return;
    }
    window.open(pdfPath, "_blank", "noopener,noreferrer");
  };

  const handleExportCase = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("User not authenticated");
      return;
    }
    try {
      setExporting(true);

      const { caseData, collectionStatus } = buildNgtCaseData(
        caseDetails,
        listItem,
        user,
        searchType,
        cookies
      );

      // Case limit check
      const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
        user.uid,
        1,
        collectionStatus === "disposed" ? "disposed" : "pending"
      );
      if (!limitResult.canProceed) {
        setCaseLimitData({
          currentCount: limitResult.currentCount,
          maxLimit: limitResult.maxLimit,
          planName: limitResult.planName,
        });
        setShowCaseLimitDialog(true);
        return;
      }

      // Duplicate check across pending / disposed
      if (caseData.caseNo) {
        for (const coll of ["pending", "disposed"]) {
          const snap = await getDocs(
            query(
              collection(db, coll),
              where("caseNo", "==", caseData.caseNo),
              where("owner", "==", user.uid)
            )
          );
          if (!snap.empty) {
            setInfoModal({
              open: true,
              title: "Duplicate Case Found",
              message: `Case ${caseData.caseNo} is already exported and saved in your profile under ${coll} cases.`,
            });
            return;
          }
        }
      }

      // Subscription metadata for pending cases beyond free tier
      let subscriptionMetadata = null;
      if (collectionStatus === "pending") {
        const pendingSnap = await getDocs(
          query(collection(db, "pending"), where("owner", "==", user.uid))
        );
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          pendingSnap.size
        );
      }

      const caseRef = await addDoc(collection(db, collectionStatus), {
        ...caseData,
        ...(subscriptionMetadata && { subscriptionMetadata }),
        timestamp: serverTimestamp(),
      });

      // Schedule 8 AM + 6 PM reminders on the next hearing date (no-op if
      // no future hearing date can be derived from the listing history).
      const userMobile = await fetchUserMobile(user.uid);
      await scheduleHearingReminders({
        user,
        caseRef,
        caseNo: caseData.caseNo,
        partyName: caseData.petitionerName,
        adverseParty: caseData.respondentName,
        nextHearingDate: caseData.nextHearingDate,
        userMobile,
        caseStatus: collectionStatus,
      });

      // Import every PDF from the listing history into the case's
      // "Orders" folder under Files. Silent on individual failures.
      const orderResult = await uploadNgtOrderPdfs({
        user,
        caseId: caseRef.id,
        caseNo: caseData.caseNo,
        caseCollection: collectionStatus,
        listingHistory: caseDetails?.listingHistory || [],
      });

      // Reference orderResult so the lint/build doesn't flag it as unused —
      // its work (the PDF imports) still happens; we just don't surface it.
      void orderResult;

      // Mirror the High Court single-export wording so the user gets the
      // same reminder reassurance regardless of court.
      setInfoModal({
        open: true,
        title: "Information",
        message:
          "Case exported successfully! For exported case the default reminder is set to 1 day before the next hearing date.",
      });
    } catch (err) {
      console.error("NGT export error:", err);
      alert(`Error exporting case: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
                        <div className="relative min-h-screen text-slate-800 p-4 md:p-8">
        <div className="mb-4"><FindCaseStepper current={4} /></div>
        <div className="flex items-center mb-6">
          <button onClick={() => navigate(-1)} className="mr-4">
            <ArrowLeft className="text-slate-600 w-6 h-6" />
          </button>
          <h1 className="text-slate-900 text-2xl md:text-3xl font-bold">NGT Case Details</h1>
        </div>

        <div className="space-y-4">
          {/* Header Card */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h2 className="text-xl font-bold break-words">{partyTitle}</h2>
              <span className="px-3 py-1 rounded-full text-sm text-white bg-blue-500">
                {caseInfo.caseStatus || listItem.status || "Unknown Status"}
              </span>
            </div>
            <hr className="border-gray-700 my-2" />
            {listItem.zonalBench && (
              <p className="text-sm text-slate-600">Zonal Bench: {listItem.zonalBench}</p>
            )}
          </div>

          {/* Case Information */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4">
            <h3 className="text-lg font-bold mb-2">Case Information</h3>
            <hr className="border-gray-700 mb-4" />
            {Object.keys(CASE_INFO_LABELS).map((key) =>
              caseInfo[key] !== undefined ? (
                <InfoRow key={key} label={CASE_INFO_LABELS[key]} value={caseInfo[key]} />
              ) : null
            )}
            {/* Render any extra keys not in the label map */}
            {Object.entries(caseInfo)
              .filter(([k]) => !CASE_INFO_LABELS[k])
              .map(([k, v]) => (
                <InfoRow key={k} label={k} value={typeof v === "object" ? JSON.stringify(v) : v} />
              ))}
          </div>

          {/* Petitioners */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4">
            <h3 className="text-lg font-bold mb-2">Petitioner(s)</h3>
            <hr className="border-gray-700 mb-4" />
            {petitioners.length > 0 ? (
              petitioners.map((p, i) => (
                <p key={i} className="py-1 text-sm">
                  {i + 1}. {p}
                </p>
              ))
            ) : (
              <p className="text-sm text-slate-500">No petitioner information available</p>
            )}
          </div>

          {/* Respondents */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4">
            <h3 className="text-lg font-bold mb-2">Respondent(s)</h3>
            <hr className="border-gray-700 mb-4" />
            {respondents.length > 0 ? (
              respondents.map((r, i) => (
                <p key={i} className="py-1 text-sm">
                  {i + 1}. {r}
                </p>
              ))
            ) : (
              <p className="text-sm text-slate-500">No respondent information available</p>
            )}
          </div>

          {/* Listing History */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4 overflow-x-auto">
            <h3 className="text-lg font-bold mb-2">Listing History</h3>
            <hr className="border-gray-700 mb-4" />
            {listingHistory.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-600">
                    <th className="py-2">S.No</th>
                    <th className="py-2">Date of Listing</th>
                    <th className="py-2">Date of Upload</th>
                    <th className="py-2">Coram</th>
                    <th className="py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listingHistory.map((h, i) => (
                    <tr key={i} className="border-t border-gray-700">
                      <td className="py-2">{h.sNo || i + 1}</td>
                      <td className="py-2">{h.dateOfListing || "N/A"}</td>
                      <td className="py-2">{h.dateOfUpload || "N/A"}</td>
                      <td className="py-2">
                        {Array.isArray(h.coram) ? h.coram.join(", ") : h.coram || "N/A"}
                      </td>
                      <td className="py-2">
                        <button
                          onClick={() => handleViewPdf(h.pdfPath)}
                          disabled={!h.pdfPath}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-500">No listing history available</p>
            )}
          </div>

          {/* IA / MA */}
          {iaMa.length > 0 && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4 overflow-x-auto">
              <h3 className="text-lg font-bold mb-2">IA / MA</h3>
              <hr className="border-gray-700 mb-4" />
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-600">
                    <th className="py-2">S.No</th>
                    <th className="py-2">Filing Number</th>
                    <th className="py-2">Case Type</th>
                    <th className="py-2">Case Number</th>
                  </tr>
                </thead>
                <tbody>
                  {iaMa.map((item, i) => (
                    <tr key={i} className="border-t border-gray-700">
                      <td className="py-2">{item.sNo || i + 1}</td>
                      <td className="py-2 break-words">{item.filingNo || "N/A"}</td>
                      <td className="py-2">{item.caseType || "N/A"}</td>
                      <td className="py-2 break-words">{item.caseNumber || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Connected Matters */}
          {connectedMatters.length > 0 && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-[20px] p-4 overflow-x-auto">
              <h3 className="text-lg font-bold mb-2">Connected Matters</h3>
              <hr className="border-gray-700 mb-4" />
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-600">
                    <th className="py-2">S.No</th>
                    <th className="py-2">Diary Number</th>
                    <th className="py-2">Case Number</th>
                    <th className="py-2">Date</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {connectedMatters.map((item, i) => (
                    <tr key={i} className="border-t border-gray-700">
                      <td className="py-2">{item.sNo || i + 1}</td>
                      <td className="py-2 break-words">{item.diaryNo || "N/A"}</td>
                      <td className="py-2 break-words">{item.caseNo || "N/A"}</td>
                      <td className="py-2">{item.date || "N/A"}</td>
                      <td className="py-2">{item.status || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Export Button */}
          <div className="flex justify-center">
            <button
              onClick={handleExportCase}
              disabled={exporting}
              className={`bg-yellow-500 text-gray-900 py-2.5 px-8 rounded-lg shadow-sm flex items-center justify-center font-bold hover:bg-gray-200 ${
                exporting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <span className="mr-2">☁</span>
              {exporting ? "Importing..." : "Import Case"}
            </button>
          </div>
        </div>
      </div>

      <InfoModal
        isOpen={infoModal.open}
        onClose={() => setInfoModal({ open: false, title: "", message: "" })}
        title={infoModal.title}
        message={infoModal.message}
      />
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </>
  );
}

export default NGTCaseDetails;
